import {
    Alert,
    AlertDescription,
    AlertIcon,
    Box,
    Button,
    ButtonGroup,
    Card,
    CardBody,
    Divider,
    Flex,
    FormControl,
    FormLabel,
    Input,
    Select,
    SimpleGrid,
    Spinner,
    Text,
    VStack,
} from "@chakra-ui/react";
import { FunctionComponent, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { createPatch } from "rfc6902";
import { EntitiesArea } from "../../../../auth";
import { EntityResponse, useClientEntity, useClientEntityUpdate, useStates } from "../../../../api";
import { useAreaPermissions } from "../../../../hooks";

type EntityAddressFormValues = {
    street1: string;
    street2: string;
    city: string;
    state: string;
    zipCode: string;
};

const SectionHeader: FunctionComponent<{ title: string; description: string }> = ({ title, description }) => (
    <Box mb={4}>
        <Text fontWeight="semibold" fontSize="sm" color="gray.700">
            {title}
        </Text>
        <Text fontSize="xs" color="gray.500" mt={0.5}>
            {description}
        </Text>
        <Divider mt={3} />
    </Box>
);

export type EntityAddressTabProps = {
    clientId: string;
    entityId: string;
};

export const EntityAddressTab: FunctionComponent<EntityAddressTabProps> = ({ clientId, entityId }) => {
    const entity = useClientEntity(clientId, entityId, {});

    if (!entity.data) {
        return (
            <Flex align="center" justify="center" py={12}>
                <Spinner />
            </Flex>
        );
    }

    return <EntityAddressForm clientId={clientId} entity={entity.data} />;
};

const EntityAddressForm: FunctionComponent<{ clientId: string; entity: EntityResponse }> = ({ clientId, entity }) => {
    const formValues = useMemo<EntityAddressFormValues>(
        () => ({
            street1: entity.street1 ?? "",
            street2: entity.street2 ?? "",
            city: entity.city ?? "",
            state: entity.state ?? "",
            zipCode: entity.zipCode ?? "",
        }),
        [entity]
    );

    const {
        setValue,
        reset,
        register,
        handleSubmit,
        formState: { isDirty },
    } = useForm<EntityAddressFormValues>();

    useEffect(() => {
        Object.entries(formValues).forEach(([key, value]) => {
            setValue(key as keyof EntityAddressFormValues, value);
        });
    }, [formValues, setValue]);

    const areaPermissions = useAreaPermissions(EntitiesArea, clientId);
    const clientEntityUpdate = useClientEntityUpdate();
    const states = useStates();

    const save = async (data: EntityAddressFormValues) => {
        const patch = createPatch(formValues, data);
        const response = await clientEntityUpdate.mutateAsync({ clientId, entityId: entity.id, patch });

        reset({
            street1: response.street1 ?? "",
            street2: response.street2 ?? "",
            city: response.city ?? "",
            state: response.state ?? "",
            zipCode: response.zipCode ?? "",
        });
    };

    return (
        <form onSubmit={handleSubmit(save)}>
            <VStack align="stretch" spacing={4}>
                {!entity.street1 && (
                    <Alert status="warning" borderRadius="md">
                        <AlertIcon />
                        <AlertDescription>Address not configured — employer address will be blank on generated PDF forms</AlertDescription>
                    </Alert>
                )}
                <Card variant="withShadow">
                    <CardBody p="1.5rem">
                        <SectionHeader
                            title="Entity Address"
                            description="The physical location for this entity. This is used for WOTC designated zone lookups."
                        />
                        <VStack align="stretch" spacing={4}>
                            <FormControl>
                                <FormLabel>Street 1</FormLabel>
                                <Input {...register("street1")} />
                            </FormControl>
                            <FormControl>
                                <FormLabel>Street 2</FormLabel>
                                <Input {...register("street2")} />
                            </FormControl>
                            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                                <FormControl>
                                    <FormLabel>City</FormLabel>
                                    <Input {...register("city")} />
                                </FormControl>
                                <FormControl>
                                    <FormLabel>State</FormLabel>
                                    <Select placeholder="Select state" {...register("state")}>
                                        {(states.data ?? []).map(state => (
                                            <option key={state.code} value={state.code}>
                                                {state.name}
                                            </option>
                                        ))}
                                    </Select>
                                </FormControl>
                                <FormControl>
                                    <FormLabel>ZIP</FormLabel>
                                    <Input {...register("zipCode")} />
                                </FormControl>
                            </SimpleGrid>
                        </VStack>
                    </CardBody>
                </Card>

                <Flex justify="flex-end">
                    <ButtonGroup>
                        <Button variant="ghost" onClick={() => reset(formValues)} isDisabled={!isDirty}>
                            Discard
                        </Button>
                        {areaPermissions.has(EntitiesArea.update) && (
                            <Button type="submit" colorScheme="blue" isDisabled={!isDirty} isLoading={clientEntityUpdate.isLoading}>
                                Save changes
                            </Button>
                        )}
                    </ButtonGroup>
                </Flex>
            </VStack>
        </form>
    );
};
